import {
	ActionIcon,
	Checkbox,
	createStyles,
	Group,
	Tooltip,
} from '@mantine/core';
import { trackEvent } from '@repo/common/analytics';
import { Icon, ListBox, Text } from '@repo/foundations';
import { includes, isEmpty, isNil } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { ANALYTICS_EVENTS } from '../constants';
import type {
	EventType,
	IInboxFilterProps,
	IInboxListFilterItemProps,
} from '../types';

const useStyles = createStyles((theme) => ({
	actionIcon: {
		'&[data-opened]': {
			backgroundColor: theme.other.getColor('fill/secondary/hover'),
			color: theme.other.getColor('icon/secondary/hover'),
		},
	},
	menuItem: {
		padding: theme.spacing['2xs'],
		margin: `0 ${theme.spacing['2xs']}`,
		width: `calc(100% - (2 * ${theme.spacing['2xs']}))`,
	},
	checkbox: {
		opacity: 0,
		'&[data-selected]': {
			opacity: '1',
		},
	},
	menuItemRoot: {
		'&:hover .mantine-Checkbox-root': {
			opacity: 1,
		},
	},
	dropdown: {
		width: '240px',
	},
}));

function InboxListFilterItem({
	event,
	selected,
	unreadNotifications,
}: IInboxListFilterItemProps) {
	const { classes, cx } = useStyles();
	const selectedArr = useMemo(
		() => (isEmpty(selected) || isNil(selected) ? [] : selected.split(',')),
		[selected]
	);

	const isSelected = useMemo(
		() => includes(selectedArr, event.value),
		[event, selectedArr]
	);

	const unreadCount = useMemo(
		() =>
			unreadNotifications.filter(
				(notification) =>
					event.values.includes(notification.event) && !notification.read
			).length,
		[unreadNotifications, event.values]
	);

	return (
		<Group spacing="xs" noWrap className={classes.menuItemRoot}>
			<Checkbox
				checked={isSelected}
				readOnly
				className={cx(!isSelected && classes.checkbox)}
				data-selected={isSelected || undefined}
			/>
			<Group
				spacing="xs"
				noWrap
				style={{ width: '100%', justifyContent: 'space-between' }}
			>
				<Text size="sm">{event.label}</Text>
				{unreadCount > 0 && (
					<Text color="text/secondary/default" size="sm">
						{unreadCount}
					</Text>
				)}
			</Group>
		</Group>
	);
}

export const InboxFilter = observer(
	({
		events,
		setEvents,
		inboxEventTypes,
		opened,
		setOpened,
		filterSearchTerm,
		setFilterSearchTerm,
		notifications,
		unreadNotifications,
		user,
		workspace,
	}: IInboxFilterProps) => {
		const { classes } = useStyles();

		const handleEventToggle = (event: EventType) => {
			const selectedArr = events ? events.split(',') : [];
			let newSelected = selectedArr;
			const wasSelected = selectedArr.includes(event.value);

			if (wasSelected) {
				newSelected = selectedArr.filter((value) => value !== event.value);
			} else {
				newSelected.push(event.value);
			}

			const eventsString = newSelected.join(',');

			trackEvent(
				ANALYTICS_EVENTS.FILTER_TOGGLE,
				{
					eventType: event.value,
					eventLabel: event.label,
					action: wasSelected ? 'remove' : 'add',
					totalSelected: newSelected.length.toString(),
					allSelected: newSelected.join(','),
				},
				user,
				workspace
			);

			setEvents(eventsString);
		};

		const handleOpenChange = (isOpened: boolean) => {
			trackEvent(
				isOpened ? ANALYTICS_EVENTS.FILTER_OPEN : ANALYTICS_EVENTS.FILTER_CLOSE,
				{
					currentFilters: events || '',
					filterCount: events ? events.split(',').length.toString() : '0',
				},
				user,
				workspace
			);

			setOpened(isOpened);
		};

		const handleFilterSearchChange = (searchTerm: string) => {
			if (searchTerm.trim()) {
				trackEvent(
					ANALYTICS_EVENTS.FILTER_SEARCH,
					{
						searchTerm: searchTerm,
						searchLength: searchTerm.length.toString(),
						totalEventTypes: inboxEventTypes.length.toString(),
					},
					user,
					workspace
				);
			}

			setFilterSearchTerm(searchTerm);
		};

		const filteredEventTypes = useMemo(() => {
			if (!filterSearchTerm) {
				return [...inboxEventTypes];
			}

			return [...inboxEventTypes].filter((event) =>
				event.label.toLowerCase().includes(filterSearchTerm.toLowerCase())
			);
		}, [inboxEventTypes, filterSearchTerm]);

		return (
			<ListBox
				opened={opened}
				onOpenChange={handleOpenChange}
				placement="bottom-start"
			>
				<ListBox.Target>
					<Tooltip label="Filter" position="bottom">
						<ActionIcon
							size="sm"
							p={4}
							className={classes.actionIcon}
							data-opened={opened || undefined}
						>
							<Icon name="filter" color="icon/primary/default" />
						</ActionIcon>
					</Tooltip>
				</ListBox.Target>
				{opened && (
					<ListBox.ItemsDropdown
						className={classes.dropdown}
						search={{
							onChange: handleFilterSearchChange,
							value: filterSearchTerm,
							placeholder: 'Filter notifications...',
						}}
						items={filteredEventTypes}
						renderItem={(event, getProps) => (
							<ListBox.Item
								key={event.value}
								{...getProps({
									onClick: () => handleEventToggle(event),
									className: classes.menuItem,
								})}
							>
								<InboxListFilterItem
									event={event}
									selected={events}
									notifications={notifications}
									unreadNotifications={unreadNotifications}
								/>
							</ListBox.Item>
						)}
					/>
				)}
			</ListBox>
		);
	}
);
